/**
 * Error logging utility that persists errors to localStorage
 * for debugging mobile crashes where console is not accessible
 */

const MAX_LOGS = 20
const STORAGE_KEY = 'app_error_logs'

export function logError(type, message, details = {}) {
    const errorLog = {
        type,
        message,
        details,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
        },
    }

    console.error(`[${type}]`, message, details)

    try {
        const existingLogs = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
        existingLogs.push(errorLog)

        // Keep only recent logs
        if (existingLogs.length > MAX_LOGS) {
            existingLogs.splice(0, existingLogs.length - MAX_LOGS)
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(existingLogs))
    } catch (storageError) {
        console.error('Failed to persist error log:', storageError)
    }
}

export function getErrorLogs() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    } catch (e) {
        console.error('Failed to retrieve error logs:', e)
        return []
    }
}

export function clearErrorLogs() {
    try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem('react_error_logs')
        console.log('Error logs cleared')
    } catch (e) {
        console.error('Failed to clear error logs:', e)
    }
}

export function downloadErrorLogs() {
    try {
        const logs = {
            errorLogs: getErrorLogs(),
            reactErrors: JSON.parse(localStorage.getItem('react_error_logs') || '[]'),
            timestamp: new Date().toISOString(),
        }

        const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `error-logs-${Date.now()}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    } catch (e) {
        console.error('Failed to download error logs:', e)
    }
}

// Setup global error handlers
export function setupGlobalErrorHandlers() {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
        logError('UNHANDLED_PROMISE_REJECTION', event.reason?.message || 'Promise rejected', {
            reason: event.reason?.stack || String(event.reason),
        })
    })

    // Catch global JavaScript errors
    window.addEventListener('error', event => {
        logError('GLOBAL_ERROR', event.message, {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack,
        })
    })

    // Log when the page becomes visible again (useful for detecting crashes)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            const lastLog = getErrorLogs().slice(-1)[0]
            const timeSinceLastError = lastLog ? Date.now() - new Date(lastLog.timestamp).getTime() : Infinity

            // If there was an error in the last 5 seconds and page is visible again, might be a crash recovery
            if (timeSinceLastError < 5000) {
                logError('PAGE_VISIBILITY', 'Page became visible after recent error', {
                    lastError: lastLog,
                })
            }
        }
    })

    console.log('Global error handlers initialized')
}

// Add helper to window for debugging in mobile browser console
if (typeof window !== 'undefined') {
    window.__debugErrors = {
        getLogs: getErrorLogs,
        clearLogs: clearErrorLogs,
        downloadLogs: downloadErrorLogs,
    }
    console.log('Debug error utilities available at window.__debugErrors')
}
